import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const route = 'IMPORT_RESULTS_MODAL';

// Initial state
const initialState = {
  isOpen: false,
  loading: false,
  results: [],
  error: null,
  transactionData: null,
};

// Async thunk for fetching transaction details
export const fetchTransactionDetails = createAsyncThunk(
  `${route}/fetchTransactionDetails`,
  async (transactionId, { rejectWithValue }) => {
    try {
      const query = `
        query ($transactionId: String!, $nextToken: String) {
          Transactions: searchTransactions(
            transactionId: $transactionId
            nextToken: $nextToken
          ) {
            nextToken
            items {
              Id
              TransactionId
              Type
              State
              Message
              NewId
              Data
              CreatedBy
              CreatedOn
              ProcessedOn
              Status
            }
          }
        }
      `;

      const response = await clientQuery(
        query,
        {
          transactionId,
          nextToken: null // Start with first page
        },
        endpoints.GRAPHQL_GENERAL,
      );

      if (!response?.Transactions) {
        throw new Error('Error fetching transaction details');
      }

      return response.Transactions;
    } catch (exc) {
      ExceptionManager(
        exc,
        'core/views/importResultsModal/redux/slice',
        'fetchTransactionDetails'
      );
      return rejectWithValue(exc.message || 'Failed to fetch transaction details');
    }
  }
);

// Action to open modal and fetch data
export const openModalWithTransaction = createAsyncThunk(
  `${route}/openModalWithTransaction`,
  async ({ transactionId, importType, status }, { dispatch }) => {
    console.log('openModalWithTransaction called with:', { transactionId, importType, status }); 
    // Open modal first
    dispatch(openModalImportResults({ transactionId, importType, status }));
    // Then fetch transaction details
    return dispatch(fetchTransactionDetails(transactionId));
  }
);

// Slice
const importResultsModalSlice = createSlice({
  name: route,
  initialState,
  reducers: {
    openModalImportResults: (state, action) => {
      state.isOpen = true;
      state.transactionData = action.payload;
      state.error = null;
    },
    closeModalImportResults: (state) => {
      state.isOpen = false;
      state.transactionData = null;
      state.error = null;
      state.results = [];
      state.loading = false;
    },
    clearErrorModalImport: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch transaction details pending
      .addCase(fetchTransactionDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Fetch transaction details fulfilled
      .addCase(fetchTransactionDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.items || [];
        state.error = null;
      })
      // Fetch transaction details rejected
      .addCase(fetchTransactionDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch transaction details';
        state.results = [];
      });
  },
});

export const { actions, reducer } = importResultsModalSlice;
export const { openModalImportResults, closeModalImportResults, clearErrorModalImport } = actions;
export default reducer;